import { Alert } from '@creditornot/cb-alert';
import { Avatar } from '@creditornot/cb-avatar';
import { Button } from '@creditornot/cb-button';
import { Checkbox } from '@creditornot/cb-checkbox';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { APIErrorNotification } from 'common/components';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { useFetchIntegrationStatusBatch } from 'modules/shared-menu/hooks/useIntegrationStatus';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';
import { SharedMenu } from 'modules/shared-menu/types';

import { ModalHeaderWithSubheader } from './ModalHeaderWithSubheader';
import {
  ImageContainer,
  NameAndAddress,
  NoteContainer,
  StyledModalFooter,
  VenueItemLeftContent,
  VenueItemRightContent,
  VenueItemRoot,
  VenueList,
} from './styles';

type Props = {
  error: any;
  onClose: () => void;
  onGoBack: () => void;
  onSubmit: (allowedVenuesIds: string[]) => void;
  selectedVenues: VenueWithSharedMenuLinkInfo[];
  sharedMenu: SharedMenu;
  submitting: boolean;
  isMenuEmpty: boolean;
};

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  font-weight: bold;
`;

const VenueItem = styled(VenueItemRoot)<{ $disabled?: boolean }>`
  ${({ $disabled = false }) => css`
    cursor: ${$disabled ? 'not-allowed' : 'pointer'};
    opacity: ${$disabled ? 0.5 : 1};
  `}
`;

const FooterButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ConfirmAndShare = ({
  error,
  submitting,
  onGoBack,
  onClose,
  onSubmit,
  selectedVenues,
  sharedMenu,
  isMenuEmpty,
}: Props) => {
  const intl = useIntl();
  const [shouldShareWithAllVenuesOverride, setShouldShareWithAllVenuesOverride] = useState(false);

  const venuesAlreadyUsingSharedMenus = useMemo(() => {
    return selectedVenues.filter(venue => venue.sharedMenuLink.scope !== 'none');
  }, [selectedVenues]);

  const {
    data: { fullMenuIntegratedVenuesIdsSet } = { fullMenuIntegratedVenuesIdsSet: new Set<string>() },
    isLoading: isLoadingBatchIntegrationStatus,
  } = useFetchIntegrationStatusBatch(selectedVenues.map(venue => venue.id));

  const venuesThatShouldNotReceiveMenu = useMemo<VenueWithSharedMenuLinkInfo[]>(() => {
    if (!fullMenuIntegratedVenuesIdsSet?.size || shouldShareWithAllVenuesOverride) {
      return [];
    }

    return selectedVenues.filter(venue => fullMenuIntegratedVenuesIdsSet.has(venue.id));
  }, [fullMenuIntegratedVenuesIdsSet, selectedVenues, shouldShareWithAllVenuesOverride]);

  const allowedVenuesIds = useMemo(() => {
    if (shouldShareWithAllVenuesOverride) {
      return selectedVenues.map(venue => venue.id);
    }
    return selectedVenues.filter(({ id }) => !fullMenuIntegratedVenuesIdsSet.has(id)).map(venue => venue.id);
  }, [fullMenuIntegratedVenuesIdsSet, selectedVenues, shouldShareWithAllVenuesOverride]);

  const totalOfMenusToBeOverridden = venuesAlreadyUsingSharedMenus.length - venuesThatShouldNotReceiveMenu.length;

  const alerts = useMemo(
    () =>
      [
        {
          show: isMenuEmpty,
          key: 'menu-empty',
          render: (
            <Alert
              key="menu-empty"
              title={intl.formatMessage({ id: 'shared-menu.link.venues-selected.menu-empty-note-label' })}
              size="medium"
              variant="warning"
            >
              <FormattedMessage id="shared-menu.link.venues-selected.menu-empty-note" />
            </Alert>
          ),
        },
        {
          show: totalOfMenusToBeOverridden > 0,
          key: 'venues-already-linked',
          render: (
            <Alert key="venues-already-linked" variant="warning" size="medium">
              <FormattedMessage
                id="listing-manager.selections.venue-management.share.confirm-view.venues-already-linked-warning"
                values={{
                  name: localize(sharedMenu.name, intl.locale),
                  count: totalOfMenusToBeOverridden,
                }}
              />
            </Alert>
          ),
        },
        {
          show: !!venuesThatShouldNotReceiveMenu.length,
          key: 'full-menu-integrated-venues',
          render: (
            <Alert
              key="full-menu-integrated-venues"
              variant="error"
              size="medium"
              title={intl.formatMessage(
                {
                  id: 'shared-menu.link.shared-menu-selected.confirm-view.venue-full-menu-integrated-title',
                },
                {
                  count: venuesThatShouldNotReceiveMenu.length,
                },
              )}
            >
              <FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.venue-full-menu-integrated-message" />
            </Alert>
          ),
        },
      ].filter(({ show }) => show),
    [intl, isMenuEmpty, sharedMenu.name, totalOfMenusToBeOverridden, venuesThatShouldNotReceiveMenu.length],
  );

  return (
    <Root>
      <ModalHeaderWithSubheader
        onClose={onClose}
        header={<FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.title" />}
        subheader={
          <FormattedMessage
            id="shared-menu.link.shared-menu-selected.confirm-view.subheader"
            values={{
              name: localize(sharedMenu.name, intl.locale),
              count: selectedVenues.length,
            }}
          />
        }
      />

      {alerts.length > 0 && <NoteContainer>{alerts.map(({ render }) => render)}</NoteContainer>}

      {(!!venuesThatShouldNotReceiveMenu.length || shouldShareWithAllVenuesOverride) && (
        <NoteContainer>
          <CheckboxContainer>
            <Checkbox
              checked={shouldShareWithAllVenuesOverride}
              onChange={() => setShouldShareWithAllVenuesOverride(!shouldShareWithAllVenuesOverride)}
            />
            <T.Body3>
              <FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.venue-full-menu-integrated-acknowledge" />
            </T.Body3>
          </CheckboxContainer>
        </NoteContainer>
      )}

      <ListHeader>
        <T.Body3>
          <FormattedMessage id="shared-menu.navigation.venues.title" />
        </T.Body3>
        <T.Body3>
          <FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.current-menu" />
        </T.Body3>
      </ListHeader>
      <VenueList>
        {selectedVenues.map(venue => (
          <VenueItem
            key={venue.id}
            $disabled={!shouldShareWithAllVenuesOverride && fullMenuIntegratedVenuesIdsSet.has(venue.id)}
          >
            <VenueItemLeftContent>
              <ImageContainer>
                <Avatar size="tiny" imgSrc={venue.listimage ?? undefined} />
              </ImageContainer>
              <NameAndAddress>
                <T.Body3>{localize(venue.name, intl.locale)}</T.Body3>
                <T.Small>{venue.address}</T.Small>
              </NameAndAddress>
            </VenueItemLeftContent>
            <VenueItemRightContent>
              {venue.sharedMenuLink.scope === 'none' && '-'}
              {venue.sharedMenuLink.scope === sharedMenu.scope.type && (
                <Tag size="small" variant="lightYellow">
                  {venue.sharedMenuLink.sharedMenuName}
                </Tag>
              )}
              {venue.sharedMenuLink.scope !== sharedMenu.scope.type && venue.sharedMenuLink.scope !== 'none' && (
                <Tag size="small" variant="lightYellow">
                  <FormattedMessage id="shared-menu.venue-list.other-scope" />
                </Tag>
              )}
            </VenueItemRightContent>
          </VenueItem>
        ))}
      </VenueList>
      <StyledModalFooter style={{ flexDirection: 'column' }}>
        {error && <APIErrorNotification error={'error'} style={{ marginBlockEnd: '16px', width: '100%' }} />}

        <FooterButtons>
          <Button
            variant="secondary"
            size="small"
            onClick={onGoBack}
            style={{ marginInlineEnd: '8px' }}
            disabled={submitting}
          >
            <FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.back-button-label" />
          </Button>
          <Button
            variant="primary"
            size="small"
            loading={submitting || isLoadingBatchIntegrationStatus}
            disabled={allowedVenuesIds.length === 0}
            onClick={() => {
              onSubmit(allowedVenuesIds);
            }}
          >
            <FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.submit-button-label" />
          </Button>
        </FooterButtons>
      </StyledModalFooter>
    </Root>
  );
};
